import { supabase } from '../lib/supabaseClient';

/**
 * Configuración de dinámicas y su VIGENCIA (0029), para el panel.
 *
 * Hasta ahora las ventanas se cargaban por SQL, así que decidir en qué tiendas y qué fechas
 * corre una promoción exigía pedírselo a quien tuviera acceso a la base. Es justo lo contrario
 * de lo que el negocio necesita: las dinámicas cambian de tienda y de fecha entre campañas, y a
 * veces dentro de la misma.
 */

export interface DinamicaRow {
  id: string;
  code: string;
  name: string;
  active: boolean;
  prize_selection: 'esperado' | 'elegido';
}

export interface VentanaRow {
  id: string;
  dynamic_id: string;
  pdv_id: string | null;
  date_from: string | null;
  date_to: string | null;
  /** ISO 8601: 1 = lunes … 7 = domingo. `null` = todos los días. */
  weekdays: number[] | null;
  active: boolean;
  /** Nombre del PDV, o `null` cuando la ventana vale para todas las tiendas. */
  pdv: { id: string; code: string; name: string } | null;
}

export async function listDinamicasDeCampania(campaignId: string): Promise<DinamicaRow[]> {
  const { data, error } = await supabase
    .from('campaign_dynamics')
    .select('id, code, name, active, prize_selection')
    .eq('campaign_id', campaignId)
    .order('sort');
  if (error) throw new Error(error.message);
  return (data ?? []) as DinamicaRow[];
}

export async function listVentanas(dynamicIds: string[]): Promise<VentanaRow[]> {
  if (dynamicIds.length === 0) return [];
  const { data, error } = await supabase
    .from('dynamic_windows')
    .select('id, dynamic_id, pdv_id, date_from, date_to, weekdays, active, pdv:pdv_id(id, code, name)')
    .in('dynamic_id', dynamicIds)
    .order('date_from');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as VentanaRow[];
}

export interface VentanaNueva {
  dynamicId: string;
  /** `null` = la dinámica vale en TODAS las tiendas de la campaña. */
  pdvId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  /** `null` o vacío = todos los días de la semana. */
  weekdays: number[] | null;
}

/**
 * Crea una ventana por cada tienda elegida. Se insertan en UNA llamada: si algo falla, no queda
 * media configuración aplicada — una dinámica vigente en 3 de las 5 tiendas es peor que no
 * haberla configurado, porque nadie nota que faltan dos.
 */
export async function crearVentanas(filas: VentanaNueva[]): Promise<void> {
  if (filas.length === 0) return;
  const { error } = await supabase.from('dynamic_windows').insert(
    filas.map((f) => ({
      dynamic_id: f.dynamicId,
      pdv_id: f.pdvId,
      date_from: f.dateFrom,
      date_to: f.dateTo,
      // Vacío se guarda como NULL ("todos los días"), no como arreglo vacío: el CHECK de la
      // 0029 exige que si viene un arreglo tenga al menos un día.
      weekdays: f.weekdays && f.weekdays.length > 0 ? f.weekdays : null,
    })),
  );
  if (error) throw new Error(error.message);
}

export async function borrarVentana(id: string): Promise<void> {
  const { error } = await supabase.from('dynamic_windows').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface AjustesDinamica {
  /**
   * Cómo se llama el indicador de ESA campaña: "Raspa y Gana", "Canjes", "Degustaciones".
   * `null` = la campaña no tiene dinámica y la columna del tablero no aplica.
   */
  kpiLabel: string | null;
  /** Meta diaria por tienda. `null` = sin meta: el tablero muestra el número pelado. */
  kpiMetaDia: number | null;
  /** Tope de canjes por cliente. En un raspa y gana es 1. */
  maxPerClient: number;
  /** Marcas de la competencia que ofrece el formulario del mercadista. */
  competitorBrands: string[];
  /**
   * Si el formulario muestra la pregunta de FOTO de la boleta (0043). Apagarla la OCULTA; las
   * fotos ya subidas se conservan.
   */
  pideFotoBoleta: boolean;
  /** Si muestra el campo del NÚMERO de boleta (0043). Independiente de la foto. */
  pideNumeroBoleta: boolean;
}

export async function getAjustesDinamica(campaignId: string): Promise<AjustesDinamica | null> {
  const { data, error } = await supabase
    .from('campaign_dynamic_settings')
    .select(`kpi_label, kpi_meta_dia, max_per_client, competitor_brands,
             pide_foto_boleta, pide_numero_boleta`)
    .eq('campaign_id', campaignId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return {
    kpiLabel: data.kpi_label,
    kpiMetaDia: data.kpi_meta_dia,
    maxPerClient: data.max_per_client,
    competitorBrands: data.competitor_brands ?? [],
    // Respaldos con el comportamiento anterior a la 0043 por si la migración no está aplicada.
    pideFotoBoleta: data.pide_foto_boleta ?? true,
    pideNumeroBoleta: data.pide_numero_boleta ?? false,
  };
}

/**
 * Guarda los ajustes de dinámica de la campaña. Es un `upsert`: la fila puede no existir todavía
 * —una campaña sin dinámicas no la tiene— y crearla al primer guardado evita pedirle al usuario
 * que "active" algo antes de configurarlo.
 *
 * Una sola tabla y una sola fila, así que no hace falta RPC: no hay estado a medias posible.
 */
export async function guardarAjustesDinamica(
  campaignId: string, a: AjustesDinamica,
): Promise<void> {
  const { error } = await supabase.from('campaign_dynamic_settings').upsert({
    campaign_id: campaignId,
    // Vacío se guarda como NULL, que es "esta campaña no tiene indicador de dinámica". Una
    // cadena vacía haría que el tablero pintara una columna con nombre en blanco.
    kpi_label: a.kpiLabel === null || a.kpiLabel.trim() === '' ? null : a.kpiLabel.trim(),
    kpi_meta_dia: a.kpiMetaDia,
    max_per_client: a.maxPerClient,
    competitor_brands: a.competitorBrands,
    pide_foto_boleta: a.pideFotoBoleta,
    pide_numero_boleta: a.pideNumeroBoleta,
  }, { onConflict: 'campaign_id' });
  if (error) throw new Error(error.message);
}

export interface DinamicaNueva {
  campaignId: string;
  name: string;
  /** `producto` = por llevar cierto producto; `monto` = por gastar cierto importe. */
  triggerKind: 'producto' | 'monto';
  /** Solo con `monto`. Con `producto` DEBE ir null: lo exige `dynamics_umbral_coherente`. */
  minAmount: number | null;
  /** Lo que la impulsadora lee en pantalla: "Por la compra de S/24 en Downy Intense Fresh". */
  triggerNote: string;
  /** `elegido` = el premio sale al azar (raspa y gana); `esperado` = está definido de antemano. */
  prizeSelection: 'esperado' | 'elegido';
  requiresClientName: boolean;
  /** Premios de la CAMPAÑA. Los que ya existan no se duplican. */
  premios: string[];
}

/**
 * Crea una dinámica con sus premios. Va por RPC (0037) y no por dos `insert`.
 *
 * El motivo es el mismo de `crearVentanas`: son dos tablas, y si la segunda falla queda una
 * dinámica **sin premios**. Esa es peor que ninguna, porque el mercadista la ve ofrecida en su
 * celular, entra a registrar y no puede terminar — el premio es obligatorio en la pantalla.
 *
 * El código (`RG_DOWNY`) lo deriva el servidor a partir del nombre y lo desempata contra las
 * dinámicas que ya tiene la campaña. No se pide en el formulario a propósito: es un identificador
 * operativo, y pedirlo sin explicar para qué sirve produce códigos como "dinamica1".
 */
export async function crearDinamica(d: DinamicaNueva): Promise<string> {
  const { data, error } = await supabase.rpc('crear_dinamica', {
    p_payload: {
      campaign_id: d.campaignId,
      name: d.name,
      trigger_kind: d.triggerKind,
      min_amount: d.triggerKind === 'monto' && d.minAmount !== null ? String(d.minAmount) : null,
      trigger_note: d.triggerNote,
      prize_selection: d.prizeSelection,
      requires_client_name: d.requiresClientName,
      premios: d.premios,
    },
  });
  if (error) throw new Error(error.message);
  return data as string;
}
